export default function POIPopup({ poi }) {
  if (!poi) return null;
  
  const { name, category, subcategory, description, metadata = {}, address, lat, lng } = poi;

  const categoryInfo = {
    metro: { icon: 'Ⓜ️', label: 'Μετρό', color: '#00a651' },
    rail: { icon: '🚂', label: 'Προαστιακός', color: '#ff9800' },
    tram: { icon: '🚊', label: 'Τραμ', color: '#f44336' },
    ev_charger: { icon: '⚡', label: 'Φορτιστής EV', color: '#10b981' },
    parking: { icon: '🅿️', label: 'Πάρκινγκ', color: '#3b82f6' },
    bike_station: { icon: '🚲', label: 'Σταθμός Ποδηλάτων', color: '#8b5cf6' }
  };

  const info = categoryInfo[category] || { icon: '📍', label: category, color: '#6b7280' };

  // Render EV Charger specific information nicely
  const renderEVChargerInfo = () => {
    if (category !== 'ev_charger' || !metadata) return null;

    const { status, evse_count, connectors, total_power, connector_types, parking_type } = metadata;

    return (
      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px' }}>⚡</span>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Χαρακτηριστικά Φόρτισης</span>
        </div>

        {/* Status Badge */}
        {status && (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ 
              display: 'inline-block',
              padding: '4px 8px', 
              borderRadius: '4px', 
              fontSize: '11px',
              fontWeight: '600',
              backgroundColor: status === 'available' ? '#d1fae5' : status === 'occupied' ? '#fee2e2' : '#e5e7eb',
              color: status === 'available' ? '#065f46' : status === 'occupied' ? '#991b1b' : '#374151'
            }}>
              {status === 'available' ? '✓ Διαθέσιμος' : status === 'occupied' ? '⚠ Κατειλημμένος' : status.toUpperCase()}
            </span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px' }}>
          {evse_count && (
            <div style={{ backgroundColor: '#f3f4f6', padding: '6px', borderRadius: '4px' }}>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>Θέσεις Φόρτισης</div>
              <div style={{ color: '#111827', fontWeight: '600', fontSize: '14px' }}>{evse_count}</div>
            </div>
          )}
          {total_power && (
            <div style={{ backgroundColor: '#f3f4f6', padding: '6px', borderRadius: '4px' }}>
              <div style={{ color: '#6b7280', fontSize: '10px' }}>Συνολική Ισχύς</div>
              <div style={{ color: '#111827', fontWeight: '600', fontSize: '14px' }}>{total_power} kW</div>
            </div>
          )}
        </div>

        {/* Connectors */}
        {connectors && Array.isArray(connectors) && connectors.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              Διαθέσιμοι Συνδετήρες ({connectors.length})
            </div>
            <div style={{ maxHeight: '120px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {connectors.map((conn, idx) => (
                <div key={idx} style={{ 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '6px',
                  fontSize: '10px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', color: '#1f2937' }}>
                      {conn.standard || conn.type || 'Unknown'}
                    </span>
                    {conn.power && (
                      <span style={{ 
                        backgroundColor: '#10b981', 
                        color: 'white', 
                        padding: '2px 6px', 
                        borderRadius: '3px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        {conn.power} kW
                      </span>
                    )}
                  </div>
                  {conn.format && (
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>
                      {conn.format}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Connector Types Summary (if no detailed connectors) */}
        {!connectors && connector_types && (
          <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>Τύποι: </span>
            {connector_types}
          </div>
        )}

        {/* Parking Type */}
        {parking_type && (
          <div style={{ marginTop: '6px', fontSize: '11px' }}>
            <span style={{ color: '#6b7280' }}>Πάρκινγκ: </span>
            <span style={{ color: '#111827', fontWeight: '500' }}>
              {parking_type === 'ON_STREET' ? 'Επί οδού' : 
               parking_type === 'PARKING_LOT' ? 'Χώρος στάθμευσης' : 
               parking_type === 'PARKING_GARAGE' ? 'Γκαράζ' : 
               parking_type === 'UNDERGROUND_GARAGE' ? 'Υπόγειο γκαράζ' : 
               parking_type}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderMetadataField = (key, value) => {
    if (value === null || value === undefined || value === '') return null;
    
    let displayValue = value;
    let displayKey = key;

    // Translate common keys
    const keyTranslations = {
      line: 'Γραμμή',
      line_name: 'Όνομα Γραμμής',
      operator: 'Χειριστής',
      accessibility: 'Προσβασιμότητα',
      socket_types: 'Τύποι Πρίζας',
      power_kw: 'Ισχύς (kW)',
      pricing: 'Τιμολόγηση',
      capacity: 'Χωρητικότητα',
      opening_hours: 'Ώρες',
      phone: 'Τηλέφωνο',
      website: 'Ιστοσελίδα',
      email: 'Email',
      service_hours: 'Ωράριο'
    };

    displayKey = keyTranslations[key] || key;

    // Format values
    if (typeof value === 'boolean') {
      displayValue = value ? '✓' : '✗';
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else if (typeof value === 'object') {
      // Format nested objects like service_hours
      if (key === 'service_hours' && value.weekday && value.weekend) {
        return (
          <div key={key} style={{ borderBottom: '1px solid #e5e7eb', paddingTop: '4px', paddingBottom: '4px' }}>
            <div style={{ fontWeight: '500', fontSize: '12px', color: '#374151' }}>{displayKey}:</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
              <div>Καθημερινές: {value.weekday}</div>
              <div>Σαββατοκύριακα: {value.weekend}</div>
            </div>
          </div>
        );
      }
      displayValue = JSON.stringify(value);
    }

    return (
      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', borderBottom: '1px solid #f3f4f6', paddingTop: '4px', paddingBottom: '4px' }}>
        <span style={{ fontWeight: '500', fontSize: '12px', color: '#374151' }}>{displayKey}:</span>
        <span style={{ fontSize: '12px', color: '#111827', textAlign: 'right' }}>{displayValue}</span>
      </div>
    );
  };

  return (
    <div style={{ width: '260px', maxWidth: '260px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '2px solid #e5e7eb' }}>
        <div style={{ fontSize: '24px' }}>{info.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#111827', lineHeight: '1.2' }}>{name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <span style={{ display: 'inline-block', padding: '2px 6px', borderRadius: '4px', backgroundColor: info.color, color: 'white', fontSize: '10px', fontWeight: '600' }}>
              {info.label}
            </span>
            {subcategory && (
              <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600' }}>{subcategory}</span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {description && (
        <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4', margin: 0 }}>{description}</p>
        </div>
      )}

      {/* EV Charger Specific Info */}
      {renderEVChargerInfo()}

      {/* Location */}
      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '6px' }}>
          <span style={{ fontSize: '14px' }}>📍</span>
          <div style={{ flex: 1, fontSize: '11px' }}>
            <div style={{ fontWeight: '500', color: '#374151', marginBottom: '2px' }}>Τοποθεσία</div>
            {address && (
              <div style={{ color: '#6b7280', marginBottom: '2px' }}>{address}</div>
            )}
            <div style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '10px' }}>
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </div>
          </div>
        </div>
      </div>

      {/* Metadata */}
      {metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0 && category !== 'ev_charger' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px' }}>ℹ️</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Πληροφορίες</span>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '6px' }}>
            {Object.entries(metadata).map(([key, value]) => 
              renderMetadataField(key, value)
            )}
          </div>
        </div>
      )}
    </div>
  );
}
